import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {QlService} from "../../../../../../core/shared/ql/service/ql.service";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";
import {NsWizardStateInterface} from "../../../../interface/ns-wizard-state.interface";
import {NsConfigCheckResultDto} from "../../../../model/dto/api/ns-config-check-result.dto";
import {NsNotificationTypeInternal} from "../../../../enum/ns-notification-type-internal.enum";
import {NsNotificationTypeChannelTypeDto} from "../../../../model/dto/entity/ns-notification-type-channel-type.dto";
import {NsNotificationTypeFieldDto} from "../../../../model/dto/entity/ns-notification-type-field.dto";

@Component({
    selector: 'ns-wizard-step-review',
    templateUrl: './ns-wizard-step-review.component.html',
    styleUrls: ['./ns-wizard-step-review.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NsWizardStepReviewComponent implements OnInit, OnChanges {

    @Input() state: NsWizardStateInterface;
    @Input() configCheckResult: NsConfigCheckResultDto;
    @Input() configCheckLoading: boolean = false;

    @Output() onEditStep = new EventEmitter<number>();
    @Output() onRefreshCheck = new EventEmitter<void>();

    // For copy mode: loaded from backend since steps 2/3 are skipped
    copiedChannels: NsNotificationTypeChannelTypeDto[] = [];
    copiedFields: NsNotificationTypeFieldDto[] = [];
    private dataLoaded: boolean = false;

    constructor(
        private qlService: QlService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadCopyDataIfNeeded();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['state'] && !this.dataLoaded) {
            this.loadCopyDataIfNeeded();
        }
    }

    private loadCopyDataIfNeeded(): void {
        if (this.dataLoaded) return;
        if (!this.state?.notificationTypeId) return;
        if (this.state.creationMode !== 'copy') return;
        if (this.state.createdChannelTypes?.length > 0 || this.state.createdFields?.length > 0) return;

        this.dataLoaded = true;
        const notificationTypeId = this.state.notificationTypeId;

        this.qlService.builder()
            .query({
                name: 'channels',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'ns.NsNotificationTypeChannelType',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'channelTypeDtoId'},
                        {name: 'channelTypeDtoName'},
                        {name: 'templateDtoName'}
                    ]
                },
                filters: [
                    FilterCriteria.create('e.notificationTypeId', FilterCriteria.cOperatorEqual, notificationTypeId)
                ]
            })
            .query({
                name: 'fields',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'ns.NsNotificationTypeField',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'name'},
                        {name: 'fieldTypeDtoName'},
                        {name: 'mandatory'}
                    ]
                },
                filters: [
                    FilterCriteria.create('e.typeId', FilterCriteria.cOperatorEqual, notificationTypeId)
                ]
            })
            .query({
                name: 'channelTypeNames',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'co.CoChannelType',
                    as: 'e',
                    fields: [
                        {name: 'id'},
                        {name: 'name'}
                    ]
                }
            })
            .execute()
            .subscribe({
                next: (result) => {
                    // Build channel type name lookup for fallback
                    const channelTypeMap = new Map<number, string>();
                    for (const raw of (result.get('channelTypeNames') || [])) {
                        channelTypeMap.set(raw['e.id'], raw['e.name']);
                    }

                    this.copiedChannels = (result.get('channels') || []).map((raw: any) => {
                        const dto = new NsNotificationTypeChannelTypeDto();
                        dto.id = raw['e.id'];
                        dto.channelTypeDtoId = raw['e.channelTypeDtoId'];
                        dto.channelTypeDtoName = raw['e.channelTypeDtoName'] || channelTypeMap.get(raw['e.channelTypeDtoId']) || '';
                        dto.templateDtoName = raw['e.templateDtoName'];
                        return dto;
                    });
                    this.copiedFields = (result.get('fields') || []).map((raw: any) => {
                        const dto = new NsNotificationTypeFieldDto();
                        dto.id = raw['e.id'];
                        dto.name = raw['e.name'];
                        dto.fieldTypeDtoName = raw['e.fieldTypeDtoName'];
                        dto.mandatory = raw['e.mandatory'];
                        return dto;
                    });
                    this.cdr.markForCheck();
                }
            });
    }

    get notificationType(): any {
        return this.state?.notificationType;
    }

    get isCopyMode(): boolean {
        return this.state?.creationMode === 'copy';
    }

    get isTemplateType(): boolean {
        return this.state?.notificationType?.notificationTypeInternal === NsNotificationTypeInternal.TEMPLATE;
    }

    get typeLabel(): string {
        return this.isTemplateType ? 'Wiederverwendbare Vorlage' : 'Einzelne Benachrichtigung';
    }

    get typeIcon(): string {
        return this.isTemplateType ? 'fa-solid fa-file-lines' : 'fa-regular fa-bell';
    }

    get channels(): NsNotificationTypeChannelTypeDto[] {
        return this.state?.createdChannelTypes?.length > 0
            ? this.state.createdChannelTypes
            : this.copiedChannels;
    }

    get fields(): NsNotificationTypeFieldDto[] {
        return this.state?.createdFields?.length > 0
            ? this.state.createdFields
            : this.copiedFields;
    }

    get channelCount(): number {
        return this.channels.length;
    }

    get fieldCount(): number {
        return this.fields.length;
    }

    editStep(step: number): void {
        this.onEditStep.emit(step);
    }

    refreshCheck(): void {
        this.onRefreshCheck.emit();
    }
}
